import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { Trophy, Medal, MagnifyingGlass, At, Users, Shield } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import AdSlot from "../components/AdSlot";
import TeamBadge from "../components/TeamBadge";
import PremierBadge from "../components/PremierBadge";
import UserAvatar from "../components/UserAvatar";
import RankCell from "../components/RankCell";

export default function Leaderboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("predictors"); // "predictors" | "tribes"
  const [rows, setRows] = useState([]);
  const [tribes, setTribes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tribesLoading, setTribesLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [serverFiltered, setServerFiltered] = useState(false);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/leaderboard");
        setRows(data.leaderboard);
      } finally { setLoading(false); }
    })();
  }, []);

  // Load tribes when tab is first opened
  useEffect(() => {
    if (tab !== "tribes" || tribes.length > 0) return;
    setTribesLoading(true);
    (async () => {
      try {
        const { data } = await apiClient.get("/leaderboard/tribes");
        setTribes(data.tribes);
      } finally { setTribesLoading(false); }
    })();
  }, [tab, tribes.length]);

  // Server-side search when 2+ chars (debounced) — only on predictors tab
  useEffect(() => {
    if (tab !== "predictors") return undefined;
    if (query.trim().length < 2) {
      if (serverFiltered) {
        (async () => {
          const { data } = await apiClient.get("/leaderboard");
          setRows(data.leaderboard);
          setServerFiltered(false);
        })();
      }
      return undefined;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await apiClient.get("/leaderboard", { params: { q: query.trim() } });
        setRows(data.leaderboard);
        setServerFiltered(true);
      } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("Leaderboard search failed:", err); }
    }, 300);
    return () => clearTimeout(handle);
  }, [query, serverFiltered, tab]);

  const podium = useMemo(() => rows.slice(0, 3), [rows]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Global table</span>
          <h1 className="font-heading text-5xl uppercase font-black mt-3">Leaderboard</h1>
        </div>
        <div className="text-sm text-white/60 max-w-md text-right">
          Predict every fixture, climb the table, top your season.
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-white/10" role="tablist">
        <TabButton testid="lb-tab-predictors" active={tab === "predictors"} onClick={() => setTab("predictors")}>
          <Users size={14} weight="bold" /> Predictors
        </TabButton>
        <TabButton testid="lb-tab-tribes" active={tab === "tribes"} onClick={() => setTab("tribes")}>
          <Shield size={14} weight="bold" /> Club Tribes
        </TabButton>
      </div>

      {tab === "predictors" && (
        <>
          {/* Search */}
          <div className="mt-8 relative max-w-md">
            <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="leaderboard-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by nickname…"
              className="w-full bg-[#0B0D11] border border-white/10 pl-11 pr-4 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow font-mono-num"
            />
          </div>

          <div className="mt-6">
            <AdSlot id="leaderboard-top" size="leaderboard" />
          </div>

          {/* Podium */}
          {!loading && podium.length > 0 && !serverFiltered && (
            <div className="mt-10 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {podium.map((r, i) => (
                <PodiumCard key={r.user_id} row={r} idx={i} />
              ))}
            </div>
          )}

          <div className="mt-10 border border-white/10">
            <div className="grid grid-cols-12 gap-2 px-4 sm:px-6 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-white/40">
              <div className="col-span-2">Rank</div>
              <div className="col-span-4">Predictor</div>
              <div className="col-span-2 text-right">Picks</div>
              <div className="col-span-2 text-right">Exact</div>
              <div className="col-span-2 text-right">Points</div>
            </div>
            {loading && <div className="p-8 text-white/50 uppercase tracking-widest text-sm">Loading…</div>}
            {!loading && rows.length === 0 && (
              <div data-testid="leaderboard-empty" className="p-12 text-center text-white/60">
                {query ? `No predictors match "${query}"` : "No predictors yet. Be the first."}
              </div>
            )}
            {rows.map((r) => {
              const isMe = user?.id === r.user_id;
              const displayName = r.username || r.name || "Anonymous";
              return (
                <div
                  key={r.user_id}
                  data-testid={`lb-row-${r.rank}`}
                  className={`grid grid-cols-12 gap-2 px-4 sm:px-6 py-4 border-b border-white/5 last:border-b-0 surface items-center ${isMe ? "bg-primary-yellow/5 border-l-2 border-l-primary-yellow" : ""}`}
                >
                  <div className="col-span-2">
                    <RankCell
                      rank={r.rank}
                      previousRank={r.previous_rank}
                      bestRank={r.best_rank}
                      icon={
                        r.rank === 1 ? <Trophy size={16} weight="fill" className="text-primary-yellow" /> :
                        r.rank === 2 ? <Medal size={16} weight="fill" className="text-white/70" /> :
                        r.rank === 3 ? <Medal size={16} weight="fill" className="text-amber-700" /> : null
                      }
                    />
                  </div>
                  <div className="col-span-4 flex items-center gap-3">
                    <UserAvatar avatar={r.avatar} size={32} border />
                    <div>
                      <div className="font-semibold uppercase tracking-wide text-sm flex items-center gap-1.5">
                        <At size={11} className="text-white/40" />
                        <span className="font-mono-num">{displayName}</span>
                        <TeamBadge team={r.favourite_team} />
                        <PremierBadge active={r.is_premier} />
                        {isMe && <span className="ml-2 text-[10px] text-primary-yellow uppercase">· You</span>}
                      </div>
                      {r.name && r.name !== r.username && (
                        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{r.name}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2 text-right font-mono-num">{r.predictions}</div>
                  <div className="col-span-2 text-right font-mono-num text-primary-yellow">{r.exact_scores}</div>
                  <div className="col-span-2 text-right font-mono-num text-2xl font-black text-primary-yellow">{r.total_points}</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "tribes" && (
        <TribesView tribes={tribes} loading={tribesLoading} myTeam={user?.favourite_team} />
      )}

      <div className="mt-10">
        <AdSlot id="leaderboard-bottom" size="leaderboard" />
      </div>
    </div>
  );
}

const TabButton = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    role="tab"
    aria-selected={active}
    className={`px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold flex items-center gap-2 border-b-2 -mb-px transition-colors ${
      active
        ? "border-primary-yellow text-primary-yellow"
        : "border-transparent text-white/50 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const TribesView = ({ tribes, loading, myTeam }) => {
  const [sort, setSort] = useState("points"); // "points" | "members"

  // Server returns sorted by points; resort client-side if needed
  const sorted = React.useMemo(() => {
    const arr = [...tribes];
    if (sort === "members") {
      arr.sort((a, b) => (b.members - a.members) || (b.total_points - a.total_points) || a.team.localeCompare(b.team));
      arr.forEach((t, i) => { t.rank = i + 1; });
    } else {
      arr.sort((a, b) => (b.total_points - a.total_points) || (b.avg_points - a.avg_points) || a.team.localeCompare(b.team));
      arr.forEach((t, i) => { t.rank = i + 1; });
    }
    return arr;
  }, [tribes, sort]);

  const totalMembers = tribes.reduce((s, t) => s + (t.members || 0), 0);
  const clubsRepresented = tribes.length;
  const mostPopular = tribes.length
    ? [...tribes].sort((a, b) => b.members - a.members)[0]
    : null;

  return (
    <>
      <p className="mt-8 text-sm text-white/60 max-w-2xl">
        Aggregate points by favourite club. Find out whose fans are really predicting best this season.
      </p>

      {/* Participation summary strip */}
      {!loading && tribes.length > 0 && (
        <div data-testid="tribes-summary" className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
          <div className="p-4 surface">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Predictors</div>
            <div className="font-mono-num text-3xl font-black text-primary-yellow mt-1">{totalMembers}</div>
          </div>
          <div className="p-4 surface">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Clubs Represented</div>
            <div className="font-mono-num text-3xl font-black text-white mt-1">{clubsRepresented} <span className="text-white/30 text-base">/ 20</span></div>
          </div>
          {mostPopular && (
            <div className="p-4 surface col-span-2 sm:col-span-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Most Popular</div>
              <div className="mt-1 flex items-center gap-2">
                <TeamBadge team={mostPopular.team} size="lg" />
                <div>
                  <div className="font-bold text-sm uppercase tracking-wide leading-tight">{mostPopular.team}</div>
                  <div className="text-xs text-white/50 font-mono-num">{mostPopular.members} {mostPopular.members === 1 ? "fan" : "fans"}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sort toggle */}
      {!loading && tribes.length > 0 && (
        <div className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/50">
          <span>Sort by</span>
          <button
            data-testid="tribes-sort-points"
            onClick={() => setSort("points")}
            className={`px-3 py-1.5 border ${sort === "points" ? "border-primary-yellow text-primary-yellow bg-primary-yellow/10" : "border-white/10 text-white/50 hover:text-white"}`}
          >
            Points
          </button>
          <button
            data-testid="tribes-sort-members"
            onClick={() => setSort("members")}
            className={`px-3 py-1.5 border ${sort === "members" ? "border-primary-yellow text-primary-yellow bg-primary-yellow/10" : "border-white/10 text-white/50 hover:text-white"}`}
          >
            Members
          </button>
        </div>
      )}

      {!loading && sorted.length >= 3 && (
        <div className="mt-6 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {sorted.slice(0, 3).map((t, i) => (
            <TribePodium key={t.team} tribe={t} idx={i} isMine={t.team === myTeam} sort={sort} />
          ))}
        </div>
      )}

      <div className="mt-10 border border-white/10">
        <div className="grid grid-cols-12 gap-2 px-4 sm:px-6 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-white/40">
          <div className="col-span-1">Rank</div>
          <div className="col-span-5">Club</div>
          <div className="col-span-2 text-right">Members</div>
          <div className="col-span-2 text-right">Avg</div>
          <div className="col-span-2 text-right">Points</div>
        </div>
        {loading && <div className="p-8 text-white/50 uppercase tracking-widest text-sm">Loading…</div>}
        {!loading && sorted.length === 0 && (
          <div data-testid="tribes-empty" className="p-12 text-center text-white/60">
            No tribes yet — be the first to pick a club on your profile.
          </div>
        )}
        {sorted.map((t) => {
          const isMine = t.team === myTeam;
          return (
            <div
              key={t.team}
              data-testid={`tribe-row-${t.rank}`}
              className={`grid grid-cols-12 gap-2 px-4 sm:px-6 py-4 border-b border-white/5 last:border-b-0 surface items-center ${isMine ? "bg-primary-yellow/5 border-l-2 border-l-primary-yellow" : ""}`}
            >
              <div className="col-span-1 flex items-center gap-2">
                {t.rank === 1 && <Trophy size={16} weight="fill" className="text-primary-yellow" />}
                {t.rank === 2 && <Medal size={16} weight="fill" className="text-white/70" />}
                {t.rank === 3 && <Medal size={16} weight="fill" className="text-amber-700" />}
                <span className="font-mono-num text-lg font-bold text-white/80">#{t.rank}</span>
              </div>
              <div className="col-span-5 flex items-center gap-3">
                <TeamBadge team={t.team} size="lg" />
                <div className="font-semibold uppercase tracking-wide text-sm">
                  {t.team}
                  {isMine && <span className="ml-2 text-[10px] text-primary-yellow uppercase">· Your tribe</span>}
                </div>
              </div>
              <div className={`col-span-2 text-right font-mono-num ${sort === "members" ? "text-2xl font-black text-primary-yellow" : "text-white/80"}`}>{t.members}</div>
              <div className="col-span-2 text-right font-mono-num text-primary-yellow">{t.avg_points}</div>
              <div className={`col-span-2 text-right font-mono-num ${sort === "points" ? "text-2xl font-black text-primary-yellow" : "text-white/80"}`}>{t.total_points}</div>
            </div>
          );
        })}
      </div>
    </>
  );
};

const TribePodium = ({ tribe, idx, isMine, sort = "points" }) => {
  const Icon = idx === 0 ? Trophy : Medal;
  const labels = ["1st", "2nd", "3rd"];
  const iconColor = idx === 0 ? "text-primary-yellow" : idx === 1 ? "text-white/70" : "text-amber-700";
  const headlineValue = sort === "members" ? tribe.members : tribe.total_points;
  const headlineLabel = sort === "members" ? "fans" : "points";
  const subline = sort === "members"
    ? `${tribe.total_points} pts · ${tribe.avg_points} avg`
    : `${tribe.members} ${tribe.members === 1 ? "fan" : "fans"} · ${tribe.avg_points} avg`;
  return (
    <div className={`p-8 surface ${idx === 0 ? "md:-translate-y-3 glow-yellow border border-primary-yellow/30" : ""} ${isMine ? "bg-primary-yellow/5" : ""}`}>
      <div className="flex items-start justify-between">
        <Icon size={28} weight="fill" className={iconColor} />
        <span className="text-[10px] uppercase tracking-widest text-white/40">{labels[idx]}</span>
      </div>
      <div className="mt-6 flex items-center gap-3">
        <TeamBadge team={tribe.team} size="lg" />
      </div>
      <div className="font-heading text-2xl uppercase font-black mt-3 truncate">{tribe.team}</div>
      <div className="mt-1 text-[10px] uppercase tracking-widest text-white/40">{subline}</div>
      <div className="font-mono-num text-5xl font-black text-primary-yellow mt-4">{headlineValue}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{headlineLabel}</div>
    </div>
  );
};

const PodiumCard = ({ row, idx }) => {
  const Icon = idx === 0 ? Trophy : Medal;
  const labels = ["1st", "2nd", "3rd"];
  const iconColor = idx === 0 ? "text-primary-yellow" : idx === 1 ? "text-white/70" : "text-amber-700";
  const displayName = row.username || row.name || "—";
  return (
    <div className={`p-8 surface ${idx === 0 ? "md:-translate-y-3 glow-yellow border border-primary-yellow/30" : ""}`}>
      <div className="flex items-start justify-between">
        <Icon size={28} weight="fill" className={iconColor} />
        <span className="text-[10px] uppercase tracking-widest text-white/40">{labels[idx]}</span>
      </div>
      <div className="font-heading text-3xl uppercase font-black mt-6 truncate font-mono-num flex items-center gap-2">
        <At size={20} className="text-white/40" />
        {displayName}
        <TeamBadge team={row.favourite_team} size="lg" />
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-widest text-white/40">{row.predictions} picks · {row.exact_scores} exact</div>
      <div className="font-mono-num text-5xl font-black text-primary-yellow mt-4">{row.total_points}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">points</div>
    </div>
  );
};
